import React, { useState, useRef, useEffect } from 'react';
import { Zap, Home, Shield, Sun, ArrowRight, Check, Sparkles, ChevronsUpDown, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useChargerCatalog } from '@/components/charger/useChargerCatalog';
import { saveSavingsEstimate } from '@/components/analytics/saveSavingsEstimate';
import GoogleRatingBadge from '@/components/reviews/GoogleRatingBadge';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };
const sans = { fontFamily: '"DM Sans", "Space Grotesk", sans-serif' };

export default function L2SavingsPanel({
  compElSavings, laderabatYearly, smartChargeSavingsYearly, solarSavingsYearly,
  totalKwh, monthlySubscription, subName, isEligibleForLaderabat, hasSolar,
  needsCharger, selectedCharger, loading,
  onCheckout,
  hasEV,
  installationChoice,
  serviceAgreement,
  wantsPostpaid,
  competitors,
  competitorName,
  selectedCompetitorSlug,
  onSelectCompetitor,
  cacheDate,
  paymentFeeSavings = 0,
  householdProps,
}) {
  const [competitorDropdownOpen, setCompetitorDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { byId: chargersById } = useChargerCatalog();

  useEffect(() => {
    if (!competitorDropdownOpen) return;
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setCompetitorDropdownOpen(false); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [competitorDropdownOpen]);

  const resolvedCharger = (() => {
    if (!selectedCharger) return null;
    if (typeof selectedCharger === 'string') return chargersById[selectedCharger] || null;
    return selectedCharger;
  })();

  const serviceAgreementValue = serviceAgreement ? 588 : 0; // ~49 kr/md market median
  // Payment fee savings are already included in compElSavings (competitor yearly_cost includes fees, enkel's does not)
  // We break it out for display only — elSavingsNet shows the remaining after separating payment fees
  const elSavingsNet = Math.max(0, compElSavings - paymentFeeSavings);
  const compTotalSavings = compElSavings + laderabatYearly + smartChargeSavingsYearly + serviceAgreementValue + solarSavingsYearly;

  // Track savings estimate for analytics
  useEffect(() => {
    if (compTotalSavings > 0 && !loading) {
      const sourcePage = window.location.pathname.includes('elbil') ? 'elbil' : 'loesning2';
      saveSavingsEstimate({
        source_page: sourcePage,
        competitor_slug: selectedCompetitorSlug,
        competitor_name: competitorName,
        total_savings_yearly: compTotalSavings,
        el_savings_yearly: compElSavings,
        laderabat_savings_yearly: laderabatYearly,
        smart_charge_savings_yearly: smartChargeSavingsYearly,
        solar_savings_yearly: solarSavingsYearly,
        service_agreement_savings_yearly: serviceAgreementValue,
        payment_fee_savings_yearly: paymentFeeSavings,
        total_kwh: totalKwh,
        has_ev: hasEV,
        has_solar: hasSolar,
        is_eligible_laderabat: isEligibleForLaderabat,
        charger_status: needsCharger ? 'need' : (serviceAgreement ? 'compatible' : 'none'),
      });
    }
  }, [compTotalSavings, selectedCompetitorSlug, loading]);

  return (
    <div className="space-y-3" style={sans}>

      {/* ═══ Order summary + checkout ═══ */}
      <div className="rounded-2xl p-5 sm:p-6 border shadow-sm" style={{ backgroundColor: '#ffffff', borderColor: 'rgba(0,0,0,0.08)' }}>
        <h4 className="text-lg font-bold text-[#21343D] mb-3" style={serif}>Ordreoversigt</h4>
        <div className="space-y-2 text-sm pb-3 border-b border-gray-100">
          <div className="flex justify-between"><span className="text-gray-600">El-aftale ({subName})</span><span className="font-medium text-gray-900">{monthlySubscription} kr./md.</span></div>
          {wantsPostpaid && <div className="flex justify-between"><span className="text-gray-600">Forbrugsafregning</span><span className="font-medium text-gray-900">0,02 kr./kWh</span></div>}
          {isEligibleForLaderabat && <div className="flex justify-between"><span className="text-gray-600">Serviceaftale + Saving Mode + Laderabat</span><span className="font-medium text-gray-900">0 kr./md.</span></div>}
          {hasSolar && <div className="flex justify-between"><span className="text-gray-600">Solcelleaftale</span><span className="font-medium text-gray-900">0 kr./md.</span></div>}
          {needsCharger && resolvedCharger && (() => {
            const chargerPrice = resolvedCharger.price || 0;
            const installPrices = { standard: 3000, existing: 1300, none: 0 };
            const installLabels = { standard: ' + Ny installation', existing: ' + Eksist. installation', none: '' };
            const installPrice = installationChoice ? (installPrices[installationChoice] ?? 0) : 0;
            const totalPrice = chargerPrice + installPrice;
            const label = resolvedCharger.name + (installationChoice ? (installLabels[installationChoice] || '') : '');
            return (
              <div className="flex justify-between pt-2 border-t border-gray-100">
                <span className="text-gray-600">{label}</span>
                <span className="font-medium text-gray-900">{totalPrice.toLocaleString('da-DK')} kr.</span>
              </div>
            );
          })()}
        </div>

        <div className="bg-gray-50 rounded-xl p-3 my-3 border border-gray-100">
          <p className="text-xs font-semibold text-[#2F424D] mb-1.5" style={sans}>Hvad sker der i næste trin?</p>
          <div className="space-y-1.5 text-xs text-gray-600" style={sans}>
            <div className="flex items-start gap-2"><div className="w-4 h-4 rounded-full bg-[#2F424D] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</div><span>Indtast adresse + startdato</span></div>
            <div className="flex items-start gap-2"><div className="w-4 h-4 rounded-full bg-[#2F424D] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</div><span>Bekræft med MitID (under 2 min.)</span></div>
            <div className="flex items-start gap-2"><div className="w-4 h-4 rounded-full bg-[#2F424D] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</div><span>Vi klarer resten inkl. opsigelse</span></div>
          </div>
        </div>
        <Button onClick={onCheckout} className="w-full bg-[#F1EBDD] hover:bg-[#E9DFC9] text-[#21343D] font-bold text-lg h-14 rounded-xl border-[1.5px] border-[#D4A62A]/55 shadow-[0_8px_22px_rgba(38,58,67,0.10)] hover:shadow-[0_10px_26px_rgba(38,58,67,0.14)] hover:border-[#D4A62A] transition-all" style={sans}>
          Fortsæt til adresse og MitID <ArrowRight className="w-5 h-5 ml-[18px] text-[#B58716]" />
        </Button>
        <div className="mt-4 text-center"><GoogleRatingBadge /></div>
        <div className="mt-3 text-center">
          <p className="text-xs font-medium text-gray-900">Ingen binding · Sikker tilmelding med MitID</p>
          <div className="flex items-center justify-center gap-4 mt-2 opacity-50">
            <img src="https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/9c767fb55_b1e5a4f12_nyt-billede-mitid-1.png" alt="MitID" className="h-4 w-auto grayscale" />
            <img src="https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/640dc5620_acc93eb17_betalingsservice_pos_RGB.png" alt="Betalingsservice" className="h-3.5 w-auto grayscale" />
          </div>
        </div>

      </div>

      {/* Disclaimer */}
      <p className="text-[10px] text-gray-400 px-1">
        *Estimeret besparelse baseret på sammenligning med {competitorName}s billigste produkt. Data fra Strømligning API{cacheDate ? ` (senest opdateret ${new Date(cacheDate).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })})` : ''}.{isEligibleForLaderabat ? ' Laderabat: 10 øre rabat af spotprisen via Saving Mode app.' : ''}{smartChargeSavingsYearly > 0 ? ' Saving Mode-besparelse: estimeret ~22% af elbil-forbrug.' : ''}
      </p>
    </div>
  );
}